import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import KamoHeader from "./KamoHeader";
import KamolacFooter from "./KamolacFooter";
import "./KamolacCatalogue.css";

/*
  Kamolac Coatings page.
  All product facts below are taken from the Kamolac catalogue (kamolac-catalogue.pdf).
  Anything the catalogue does not state (coverage, drying time, packaging, pricing,
  certifications) is intentionally NOT shown.
*/

const SITE = "https://www.kamdhenupaints.com";
const PAGE_URL = SITE + "/kamolac-coating";
const IMG = (name) => process.env.PUBLIC_URL + "/images/kamolac/" + name + ".webp";

const PRODUCTS = [
  {
    id: "pu-wood-finishes",
    name: "PU Wood Finishes",
    tagline: "The Gold Standard",
    category: "Wood coatings",
    img: "pu-wood-finishes", w: 362, h: 240,
    alt: "Study room with wooden shelving and desk finished with Kamolac PU Wood Finishes",
    summary:
      "Premium polyurethane (PU) coatings for wood, available in three sheens so the surface can look natural or glass-like.",
    features: [
      ["Matt", "Pure, organic elegance."],
      ["Semi-Gloss", "The perfect satin balance."],
      ["High-Gloss", "A deep, liquid-glass reflection."],
    ],
    application: "Wood surfaces such as furniture and interior joinery",
    link: "/pu-wood-finish",
    linkText: "View PU Wood Finish details",
  },
  {
    id: "hdx-masterclass",
    name: "HD-X Masterclass",
    tagline: "Crystal Clarity",
    category: "Wood coatings",
    img: "hdx-masterclass", w: 347, h: 240,
    alt: "Clear-finished round wooden tabletop with a plant, showing Kamolac HD-X Masterclass clarity",
    summary:
      "A clear wood finish with water-white transparency that never turns yellow.",
    features: [
      ["Water-white transparency", "Never turns yellow."],
      ["Sheens", "Available from Flat Matt to Mirror High-Gloss."],
    ],
    application: "Wood surfaces where a clear, natural look is wanted",
    link: "/hdx-masterclass",
    linkText: "View HD-X Masterclass details",
  },
  {
    id: "eco-friendly-water-based",
    name: "Eco-Friendly Water-Based Coatings",
    tagline: "Conscious Luxury",
    category: "Wood coatings",
    img: "eco-water-based", w: 362, h: 240,
    alt: "Living space with a wooden armchair, suited to Kamolac water-based wood coatings",
    summary:
      "Water-based wood coatings using advanced resin technology, designed for homes and occupied spaces.",
    features: [
      ["Low odour, low VOC", "Advanced resin technology with zero harsh odors and low VOCs."],
      ["Safe for homes", "Ideal for kids' rooms and occupied spaces."],
      ["Fast curing", "Multiple coats in a single day."],
    ],
    application: "Wood in homes, including kids' rooms and occupied spaces",
    link: "/eco-friendly-water-based",
    linkText: "View water-based coating details",
  },
  {
    id: "metal-and-glass-coatings",
    name: "Metal & Glass Coatings",
    tagline: "Industrial Edge",
    category: "Metal and glass coatings",
    img: "metal-glass-coatings", w: 540, h: 187,
    alt: "Black-framed glass partition in a modern office, an application for Kamolac metal and glass coatings",
    summary:
      "Coatings for steel, aluminium and glass surfaces, covering protection and colour.",
    features: [
      ["Metal", "Anti-corrosive clear and vibrant colour coats for steel and aluminium."],
      ["Glass", "Back-painted colours and “Easy-Clean” hydrophobic protective layers."],
    ],
    application: "Steel, aluminium and glass",
    link: "/contact",
    linkText: "Enquire about metal & glass coatings",
  },
  {
    id: "wood-stains",
    name: "Wood Stains",
    tagline: "Deep Soul",
    category: "Wood coatings",
    img: "wood-stain-bottle", w: 177, h: 367,
    alt: "Kamolac Wood Stain bottle for interior wood",
    summary:
      "Premium, high-performance stains that enhance the natural beauty of wood and give it rich, long-lasting colour while preserving its natural grain.",
    features: [
      ["Enhances natural beauty", "Keeps the natural grain visible."],
      ["Deep penetration", "Durability, excellent penetration and a superior finish."],
      ["Rich & lasting colours", "From classic Teak and Walnut to modern Charcoal and Slate."],
      ["Interior & exterior", "Suitable for interior and exterior wood surfaces."],
    ],
    application: "Interior and exterior wood surfaces",
    link: "#stain-shades",
    linkText: "See the stain shade list",
  },
  {
    id: "pu-pigmented-solutions",
    name: "PU Pigmented Solutions",
    tagline: "Colours for Every Space",
    category: "Wood coatings",
    img: "pu-pigmented-kitchen", w: 733, h: 733,
    alt: "Modern kitchen with teal, yellow and blue cabinetry finished in Kamolac PU Pigmented Solutions",
    summary:
      "Coloured PU finishes for wood surfaces, with advanced tinting technology that can match any shade. Ideal for modern kitchens, furniture and premium interiors.",
    features: [
      ["Wide range of colours", "A wide spectrum of rich, long-lasting colours."],
      ["Accurate shade matching", "Advanced tinting: any colour, any shade."],
      ["Durable & high coverage", "Excellent coverage and superior durability."],
      ["Premium smooth finish", "A smooth, elegant finish."],
    ],
    application: "Wood surfaces in modern kitchens, furniture and premium interiors",
    link: "/contact",
    linkText: "Enquire about colour matching",
  },
  {
    id: "italian-designer-wall-finishes",
    name: "Italian Designer Wall Finishes",
    tagline: "Walls that Reflect Your World",
    category: "Wall finishes",
    img: "italian-lounge", w: 707, h: 550,
    alt: "Lounge chair in front of a textured designer wall finish",
    summary:
      "Textured Italian designer wall finishes that blend Italian aesthetics with lasting beauty, for walls and exteriors.",
    features: [
      ["Premium Italian finishes", "Italian aesthetics for modern living."],
      ["Rich textures", "Five signature textures."],
      ["Elegant colours", "Colourants are added to the base paint."],
      ["Interiors & exteriors", "Suitable for interiors and exteriors."],
      ["Durable & long lasting", "Lasting beauty."],
    ],
    application: "Homes, offices, hotels, showrooms and commercial spaces",
    link: "/contact",
    linkText: "Enquire about designer wall finishes",
  },
];

const TEXTURES = [
  ["Dholpur Classic", "dholpur-classic"],
  ["Elegant Stone", "elegant-stone"],
  ["Rustic Grande", "rustic-grande"],
  ["Cerastone", "cerastone"],
  ["Granite Stone", "granite-stone"],
];

// Shade codes, names and ratios as printed in the catalogue. Swatch colours are sampled from it.
const STAINS = [
  ["KMU2601", "Lemon", "1:5*", "#b6662c"],
  ["KMU2603", "Red", "1:5*", "#af1b19"],
  ["KMU2606", "Dark Blue", "1:4*", "#014451"],
  ["KMU2611", "Orange", "1:5*", "#c92f1d"],
  ["KMU2613", "Antique Nuts", "1:10*", "#3e2520"],
  ["KMU2615", "Teak", "1:5*", "#924a1f"],
  ["KMU2617", "Honey", "1:10*", "#a95523"],
  ["KMU2619", "Oak", "1:10*", "#6a3418"],
  ["KMU2621", "Antique Oa", "1:10*", "#713b1f"],
  ["KMU2623", "Cherry", "1:10*", "#892813"],
  ["KMU2625", "Chestnut", "1:10*", "#83421d"],
  ["KMU2627", "Nut", "1:10*", "#642a1c"],
  ["KMU2629", "Dark Nut", "1:10*", "#3c1a17"],
  ["KMU2631", "Mahogany", "1:10*", "#5c1d13"],
  ["KMU2633", "Poysandtr", "1:10*", "#451719"],
  ["KMU2635", "Teak", "1:10*", "#72381c"],
  ["KMU2637", "Ripe Cherry", "1:10*", "#7a1d0c"],
  ["KMU2641", "Light Net", "—", "#914b26"],
  ["KMU2643", "Green", "1:5*", "#354a29"],
];

const BENEFITS = [
  ["UV Resistance", "Coatings that stay vibrant under the harsh Indian sun.", "M12 4V2m0 20v-2M4 12H2m20 0h-2M5.6 5.6 4.2 4.2m15.6 15.6-1.4-1.4M5.6 18.4l-1.4 1.4m15.6-15.6-1.4 1.4M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z"],
  ["Hardness & Flexibility", "A film tough enough to resist dents but flexible enough to move with the seasons.", "M12 3 4 6v6c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V6l-8-3zm-1 12-3-3 1.4-1.4L11 12.2l4.6-4.6L17 9l-6 6z"],
  ["Expertly Crafted", "Formulations that have made Kamdhenu a household name in quality.", "M12 3 3 9l9 12 9-12-9-6zm0 2.3L17.5 9H6.5L12 5.3z"],
  ["Premium sheen range", "From Matt and Semi-Gloss to Mirror High-Gloss.", "M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4L12 2z"],
  ["Low odour, low VOC option", "Water-based coatings with advanced resin technology.", "M12 2C8 8 5 11 5 15a7 7 0 0 0 14 0c0-4-3-7-7-13z"],
  ["Accurate shade matching", "Advanced tinting technology to match any shade.", "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4v6l4 2"],
];

const AT_A_GLANCE = [
  ["PU Wood Finishes", "PU wood coating", "Wood", "Matt, Semi-Gloss, High-Gloss"],
  ["HD-X Masterclass", "Clear wood finish", "Wood", "Flat Matt to Mirror High-Gloss"],
  ["Eco-Friendly Water-Based", "Water-based wood coating", "Wood (homes, kids' rooms, occupied spaces)", "Not specified in catalogue"],
  ["Metal Coatings", "Anti-corrosive clear and colour coats", "Steel and aluminium", "Clear and vibrant colours"],
  ["Glass Coatings", "Back-painted colours; Easy-Clean hydrophobic layers", "Glass", "Colours; protective layers"],
  ["Wood Stains", "Wood stain (shade list below)", "Interior and exterior wood", "Shades KMU2601–KMU2643"],
  ["PU Pigmented Solutions", "Coloured PU finish with tinting", "Wood (kitchens, furniture, premium interiors)", "Any shade by tinting"],
  ["Italian Designer Wall Finishes", "Textured wall finish (base paint; colourants extra)", "Interior and exterior walls", "Five signature textures"],
];

const APPLICATIONS = [
  ["Modern kitchens", "PU Pigmented Solutions", "pu-pigmented-solutions", "pu-pigmented-kitchen", 733, 733, "Teal, yellow and blue kitchen cabinetry"],
  ["Furniture and premium interiors", "PU Wood Finishes, HD-X Masterclass, PU Pigmented Solutions", "pu-wood-finishes", "hero-range", 1189, 1068, "Wooden cabinet finished with Kamolac coatings"],
  ["Kids' rooms and occupied spaces", "Eco-Friendly Water-Based Coatings", "eco-friendly-water-based", "eco-water-based", 362, 240, "Living space with wooden armchair"],
  ["Interior and exterior walls", "Italian Designer Wall Finishes", "italian-designer-wall-finishes", "italian-exterior", 784, 785, "Modern home exterior with textured wall finish"],
];

const SEGMENTS = ["Homes", "Offices", "Hotels", "Showrooms", "Commercial Spaces"];

const FAQS = [
  ["What is Kamolac?",
    "Kamolac is the premium coating range by Kamdhenu Paints, engineered to transform wood, metal and glass surfaces with protective, high-quality finishes. The range is positioned as “Wood • Metal • Glass Coatings”."],
  ["What types of coatings does Kamolac offer?",
    "The catalogue lists PU Wood Finishes, HD-X Masterclass, Eco-Friendly Water-Based coatings, Metal & Glass Coatings, Wood Stains, PU Pigmented Solutions and Italian Designer Wall Finishes."],
  ["Which sheens are available in Kamolac PU Wood Finishes?",
    "PU Wood Finishes come in Matt, Semi-Gloss and High-Gloss. HD-X Masterclass is available from Flat Matt to Mirror High-Gloss."],
  ["Which Kamolac coating is suitable for kids' rooms?",
    "The catalogue describes the Eco-Friendly Water-Based range as safe for homes and ideal for kids' rooms and occupied spaces, with zero harsh odors and low VOCs."],
  ["Can Kamolac match any colour?",
    "Kamolac PU Pigmented Solutions use advanced tinting technology that can match any shade, for wood surfaces in kitchens, furniture and premium interiors."],
  ["What does Kamolac offer for metal and glass?",
    "For metal, anti-corrosive clear and vibrant colour coats for steel and aluminium. For glass, back-painted colours and “Easy-Clean” hydrophobic protective layers."],
  ["Are Kamolac Italian Designer Wall Finishes sold ready-tinted?",
    "No. The tin is labelled “Only for Base Paint, add colourants, colourants cost extra.” Five signature textures are shown: Dholpur Classic, Elegant Stone, Rustic Grande, Cerastone and Granite Stone."],
  ["Does Kamdhenu provide training for applicators?",
    "Yes. The Free Training Center runs hands-on workshops for contractors and painters on spray techniques and mixing ratios."],
  ["What are the coverage, drying time and packaging details?",
    "These details are not stated in the Kamolac catalogue. Please contact Kamdhenu Paints for product information."],
];

function jsonLd() {
  const org = {
    "@type": "Organization",
    "@id": SITE + "/#organization",
    name: "Kamdhenu Paints",
    url: SITE,
    brand: { "@type": "Brand", name: "Kamolac" },
  };
  return {
    "@context": "https://schema.org",
    "@graph": [
      org,
      {
        "@type": "WebPage",
        "@id": PAGE_URL + "#webpage",
        url: PAGE_URL,
        name: "Kamolac Wood, Metal & Glass Coatings | Kamdhenu Paints",
        isPartOf: { "@id": SITE + "/#website" },
        about: { "@type": "Brand", name: "Kamolac" },
        publisher: { "@id": SITE + "/#organization" },
        primaryImageOfPage: SITE + "/images/kamolac/hero-range.webp",
      },
      { "@type": "WebSite", "@id": SITE + "/#website", url: SITE, name: "Kamdhenu Paints" },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
          { "@type": "ListItem", position: 2, name: "Kamolac Coatings", item: PAGE_URL },
        ],
      },
      {
        "@type": "ItemList",
        name: "Kamolac coating range",
        itemListElement: PRODUCTS.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Product",
            name: "Kamolac " + p.name,
            description: p.summary,
            category: p.category,
            image: SITE + "/images/kamolac/" + p.img + ".webp",
            brand: { "@type": "Brand", name: "Kamolac" },
            manufacturer: { "@id": SITE + "/#organization" },
            url: PAGE_URL + "#" + p.id,
          },
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQS.map(([q, a]) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
    ],
  };
}

function Icon({ d }) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Pic({ name, w, h, alt, eager, className }) {
  return (
    <img
      src={IMG(name)}
      width={w}
      height={h}
      alt={alt}
      className={className}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      {...(eager ? { fetchpriority: "high" } : {})}
    />
  );
}

function KamolacCatalogue() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <title>Kamolac Wood, Metal & Glass Coatings | Kamdhenu Paints</title>
        <meta
          name="description"
          content="Kamolac by Kamdhenu Paints: PU wood finishes, HD-X Masterclass, water-based coatings, metal and glass coatings, wood stains, PU pigmented colours and Italian designer wall finishes."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Kamdhenu Paints" />
        <meta property="og:title" content="Kamolac Wood, Metal & Glass Coatings | Kamdhenu Paints" />
        <meta
          property="og:description"
          content="Premium coating range by Kamdhenu Paints for wood, metal and glass: PU finishes, water-based coatings, wood stains, pigmented colours and Italian wall finishes."
        />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={SITE + "/images/kamolac/hero-range.webp"} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd())}</script>
      </Helmet>

      <KamoHeader />

      <div className="kcx">
        <nav className="kcx-crumbs" aria-label="Breadcrumb">
          <ol>
            <li><Link to="/">Home</Link></li>
            <li aria-current="page">Kamolac Coatings</li>
          </ol>
        </nav>

        <main id="main">
          {/* 1. HERO */}
          <section className="kcx-hero" aria-labelledby="kcx-h1">
            <div className="kcx-wrap kcx-hero-grid">
              <div className="kcx-hero-copy">
                <p className="kcx-eyebrow">Wood &bull; Metal &bull; Glass Coatings</p>
                <h1 id="kcx-h1">Kamolac: premium coatings for wood, metal and glass</h1>
                <p className="kcx-lead">
                  Kamolac, the premium coating range by Kamdhenu Paints, brings world-class finishes
                  engineered to transform wood, metal and glass into masterpieces. Don&rsquo;t just paint.
                  Protect &amp; perfect.
                </p>
                <ul className="kcx-chips">
                  <li>UV resistance</li>
                  <li>Hardness &amp; flexibility</li>
                  <li>Free applicator training</li>
                </ul>
              </div>
              <div className="kcx-hero-media">
                <Pic
                  name="hero-range" w={1189} h={1068} eager
                  alt="Four Kamolac coating tins on a polished wooden cabinet with a spray gun beside them"
                />
              </div>
            </div>
          </section>

          {/* 2. ABOUT */}
          <section id="about" className="kcx-sec" aria-labelledby="kcx-about-h">
            <div className="kcx-wrap kcx-split">
              <div>
                <p className="kcx-eyebrow">The future of finishing</p>
                <h2 id="kcx-about-h">Where innovation meets elegance</h2>
                <p>
                  Kamolac is the premium coating range by Kamdhenu Paints, engineered to deliver surface
                  protection and aesthetic brilliance on wood, metal and glass.
                </p>
                <p>
                  From the mirror-shine of High-Gloss PU to the sustainable clarity of water-based coatings,
                  Kamolac is positioned as the professional&rsquo;s choice for luxury finishes.
                </p>
                <ul className="kcx-ticks">
                  <li>Premium range from Kamdhenu Paints</li>
                  <li>Finishes for wood, metal and glass</li>
                  <li>Sheens from Matt to Mirror High-Gloss</li>
                  <li>Water-based option with low VOCs</li>
                </ul>
              </div>
              <figure className="kcx-figure">
                <Pic name="about-interior" w={1000} h={572} alt="Dining room with wooden sideboard, table and chairs on a wooden floor" />
              </figure>
            </div>
          </section>

          {/* 3. WHY */}
          <section id="why" className="kcx-sec kcx-dark" aria-labelledby="kcx-why-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Why Kamolac?</p>
              <h2 id="kcx-why-h">Built to look premium and last</h2>
              <ul className="kcx-cards kcx-cards-3">
                {BENEFITS.map(([t, d, ic]) => (
                  <li key={t} className="kcx-card">
                    <span className="kcx-ico"><Icon d={ic} /></span>
                    <h3>{t}</h3>
                    <p>{d}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 4. CATEGORIES */}
          <section id="categories" className="kcx-sec" aria-labelledby="kcx-cat-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">The Kamolac spectrum</p>
              <h2 id="kcx-cat-h">Product categories</h2>
              <p className="kcx-sub">Signature ranges to refine every surface.</p>
              <ul className="kcx-cards kcx-cards-3 kcx-cat-list">
                {PRODUCTS.map((p) => (
                  <li key={p.id} className="kcx-cat">
                    <a href={"#" + p.id} className="kcx-cat-link">
                      <span className="kcx-cat-tag">{p.category}</span>
                      <h3>{p.name}</h3>
                      <p className="kcx-cat-tagline">{p.tagline}</p>
                      <p>{p.summary}</p>
                      <p className="kcx-cat-app"><strong>Key application:</strong> {p.application}</p>
                      <span className="kcx-more">Explore {p.name} <span aria-hidden="true">&rarr;</span></span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 5. PRODUCT SHOWCASE (all content in static HTML, no tabs) */}
          <section id="products" className="kcx-sec kcx-alt" aria-labelledby="kcx-prod-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Product showcase</p>
              <h2 id="kcx-prod-h">Kamolac products in detail</h2>
              {PRODUCTS.map((p, i) => (
                <article key={p.id} id={p.id} className={"kcx-product" + (i % 2 ? " kcx-rev" : "")}>
                  <div className="kcx-product-media">
                    <Pic name={p.img} w={p.w} h={p.h} alt={p.alt} className={p.w < 300 ? "kcx-small" : undefined} />
                  </div>
                  <div className="kcx-product-body">
                    <p className="kcx-cat-tag">{p.category}</p>
                    <h3>Kamolac {p.name}</h3>
                    <p className="kcx-cat-tagline">{p.tagline}</p>
                    <p>{p.summary}</p>
                    <dl className="kcx-feat">
                      {p.features.map(([k, v]) => (
                        <div key={k}>
                          <dt>{k}</dt>
                          <dd>{v}</dd>
                        </div>
                      ))}
                    </dl>
                    <p><strong>Main application:</strong> {p.application}.</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* Wood stain shades */}
          <section id="stain-shades" className="kcx-sec" aria-labelledby="kcx-stain-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Wood Stains</p>
              <h2 id="kcx-stain-h">Kamolac Wood Stain shades</h2>
              <p className="kcx-sub">
                Colours that bring wood to life. Shade codes, names and ratios are listed as printed in the
                Kamolac catalogue; on-screen colours are approximate.
              </p>
              <ul className="kcx-stains">
                {STAINS.map(([code, name, ratio, hex]) => (
                  <li key={code}>
                    <span className="kcx-chip" style={{ background: hex }} aria-hidden="true" />
                    <span className="kcx-stain-txt">
                      <strong>{code}</strong>
                      <span>{name}</span>
                      <span className="kcx-ratio">Ratio {ratio}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="kcx-note">
                * Ratio as printed in the catalogue; the catalogue does not define it further. Contact
                Kamdhenu Paints for mixing guidance.
              </p>
            </div>
          </section>

          {/* Italian textures */}
          <section id="textures" className="kcx-sec kcx-alt" aria-labelledby="kcx-tex-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Italian Designer Wall Finishes</p>
              <h2 id="kcx-tex-h">Signature textures</h2>
              <p className="kcx-sub">
                Inspired spaces, timeless finishes: elegant textures and lasting beauty for walls and exteriors.
              </p>
              <ul className="kcx-tex">
                {TEXTURES.map(([n, f]) => (
                  <li key={f}>
                    <img src={IMG("texture-" + f)} width="184" height="184" loading="lazy" decoding="async" alt={n + " texture swatch"} />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
              <div className="kcx-split kcx-tex-split">
                <figure className="kcx-figure">
                  <Pic name="italian-exterior" w={784} h={785} alt="Modern home exterior with an arched terrace finished in a textured Italian wall finish" />
                </figure>
                <div>
                  <h3>Suitable for interiors and exteriors</h3>
                  <p>
                    The tin is labelled &ldquo;Only for Base Paint, add colourants, colourants cost extra.&rdquo;
                  </p>
                  <img className="kcx-bucket" src={IMG("italian-bucket")} width="419" height="485" loading="lazy" decoding="async" alt="Kamolac Italian Designer Finishes tin" />
                </div>
              </div>
            </div>
          </section>

          {/* 6. APPLICATIONS */}
          <section id="applications" className="kcx-sec" aria-labelledby="kcx-app-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Applications</p>
              <h2 id="kcx-app-h">Where Kamolac is used</h2>
              <ul className="kcx-cards kcx-cards-2">
                {APPLICATIONS.map(([t, prod, id, img, w, h, alt]) => (
                  <li key={t} className="kcx-app">
                    <Pic name={img} w={w} h={h} alt={alt} />
                    <div>
                      <h3>{t}</h3>
                      <p>Recommended: <a href={"#" + id}>{prod}</a></p>
                    </div>
                  </li>
                ))}
              </ul>
              <h3 className="kcx-h3-gap">Wall finishes are shown for</h3>
              <ul className="kcx-pills">
                {SEGMENTS.map((s) => <li key={s}>{s}</li>)}
              </ul>
            </div>
          </section>

          {/* 7/8. AT A GLANCE */}
          <section id="specs" className="kcx-sec kcx-alt" aria-labelledby="kcx-spec-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Technical information</p>
              <h2 id="kcx-spec-h">Kamolac range at a glance</h2>
              <p className="kcx-sub">
                Only details stated in the Kamolac catalogue are listed. Coverage, drying time, packaging and
                other technical values are not given there.
              </p>
              <table className="kcx-table">
                <caption className="kcx-sr">Kamolac products, type, surface and finish options</caption>
                <thead>
                  <tr><th scope="col">Product</th><th scope="col">Type</th><th scope="col">Surface / use</th><th scope="col">Finish / colour</th></tr>
                </thead>
                <tbody>
                  {AT_A_GLANCE.map(([a, b, c, d]) => (
                    <tr key={a}>
                      <th scope="row" data-label="Product">{a}</th>
                      <td data-label="Type">{b}</td>
                      <td data-label="Surface / use">{c}</td>
                      <td data-label="Finish / colour">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* GALLERY */}
          <section id="gallery" className="kcx-sec" aria-labelledby="kcx-gal-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Gallery</p>
              <h2 id="kcx-gal-h">Finished spaces</h2>
              <ul className="kcx-gallery">
                <li><Pic name="pu-pigmented-kitchen" w={733} h={733} alt="Kitchen with teal, yellow and blue cabinets" /></li>
                <li><Pic name="italian-lounge" w={707} h={550} alt="Lounge chair beside a textured designer wall" /></li>
                <li><Pic name="italian-living" w={459} h={330} alt="Living room with a textured Italian wall finish and sofa" /></li>
                <li><Pic name="why-kamolac-table" w={613} h={370} alt="Wooden table with a plant and books in daylight" /></li>
                <li><Pic name="tinting-station" w={262} h={563} alt="Kamolac tinting machine with colour dispensers and shade cards" /></li>
                <li><Pic name="italian-texture-strip" w={784} h={167} alt="Five textured wall finish samples in sand, brown, white and pink tones" /></li>
              </ul>
              <p className="kcx-note">
                Advanced tinting technology: any colour, any shade, your choice. Precision tinting, infinite possibilities.
              </p>
            </div>
          </section>

          {/* QUALITY / TRAINING */}
          <section id="training" className="kcx-sec kcx-dark" aria-labelledby="kcx-train-h">
            <div className="kcx-wrap">
              <p className="kcx-eyebrow">Support for professionals</p>
              <h2 id="kcx-train-h">Free applicator training</h2>
              <p className="kcx-sub kcx-sub-light">
                We don&rsquo;t just sell paint, we build experts. The Free Training Center provides hands-on
                workshops for contractors and painters to master spray techniques and mixing ratios.
              </p>
              <ul className="kcx-cards kcx-cards-3">
                <li className="kcx-card"><h3>Audience</h3><p>Contractors, architects and interior designers.</p></li>
                <li className="kcx-card"><h3>Activities</h3><p>Product discussions, displays and demonstrations.</p></li>
                <li className="kcx-card"><h3>Business outcome</h3><p>Accelerate trials, recommendations and channel conversion.</p></li>
              </ul>
              <h3 className="kcx-h3-gap">Experience Centre activation</h3>
              <p className="kcx-sub-light">Experience Centre activation is turning interest into confidence.</p>
              <ul className="kcx-cards kcx-cards-3 kcx-exp">
                <li>
                  <Pic name="training-product-discussion" w={358} h={320} alt="Two visitors reviewing pigmented coating finishes at an Experience Centre" />
                  <h4>Product discussion</h4>
                  <p>Visitors reviewing pigmented coating finishes.</p>
                </li>
                <li>
                  <Pic name="training-special-finishes" w={340} h={320} alt="Group discussing special finishes on a display wall" />
                  <h4>Special finishes</h4>
                  <p>Product discussions, displays and demonstrations.</p>
                </li>
                <li>
                  <Pic name="training-experience-visit" w={360} h={320} alt="Visitors at an Experience Centre looking at coloured coating panels" />
                  <h4>Experience Centre visit</h4>
                  <p>Accelerate trials, recommendations and channel conversion.</p>
                </li>
              </ul>
            </div>
          </section>

          {/* 12. FAQ */}
          <section id="faq" className="kcx-sec" aria-labelledby="kcx-faq-h">
            <div className="kcx-wrap kcx-narrow">
              <p className="kcx-eyebrow">FAQ</p>
              <h2 id="kcx-faq-h">Kamolac coatings: frequently asked questions</h2>
              <div className="kcx-faq">
                {FAQS.map(([q, a]) => (
                  <div key={q}>
                    <h3>{q}</h3>
                    <p>{a}</p>
                  </div>
                ))}
              </div>
              <p>
                More questions? See the <Link to="/faq">Kamdhenu Paints FAQ</Link> or find a{" "}
                <Link to="/dealer">dealer near you</Link>.
              </p>
            </div>
          </section>

          {/* 13. CTA */}
          <section className="kcx-cta" aria-labelledby="kcx-cta-h">
            <div className="kcx-wrap">
              <h2 id="kcx-cta-h">Looking for the right coating solution?</h2>
              <p>
                Contact Kamdhenu Paints for Kamolac product information, application guidance or enquiries.
              </p>
            </div>
          </section>
        </main>
      </div>

      <KamolacFooter />
    </>
  );
}

export default KamolacCatalogue;
